<script setup lang="ts">
/**
 * A single-line text field whose text the caller draws — a formula with its
 * references in colour, code with its tokens highlighted. The default slot
 * renders the same text as `v-model` (coloured spans); the native input lies
 * on top of it with transparent text and owns the caret, the selection, IME
 * and every input event. The field keeps both layers aligned: one font, one
 * inset, and the slot layer follows the input's horizontal scroll.
 *
 * `frame="field"` (default) is a bordered field with the kit's focus ring.
 * `frame="none"` draws no frame: an editor laid over a canvas cell, which the
 * caller positions, sizes and frames. The text takes the element's font (a
 * cell editor passes the cell's font in `style`); `paddingInline` is the inset
 * of both layers (a cell editor matches its painter's text inset).
 *
 * `readonly` shows the text in the input itself, muted and without a caret,
 * and hides the slot. Attributes and listeners go to the input, `class` and
 * `style` to the outer element. `inputElement` is exposed for caret work
 * (`selectionStart`, `setSelectionRange`, `focus`).
 */
import { computed, nextTick, ref, useAttrs, watch } from 'vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    frame?: 'field' | 'none';
    readonly?: boolean;
    placeholder?: string;
    /** Inset of the text in both layers (a CSS length); the field's default otherwise. */
    paddingInline?: string;
  }>(),
  { frame: 'field', readonly: false, placeholder: undefined, paddingInline: undefined }
);

const model = defineModel<string>({ required: true });

const attrs = useAttrs();
const inputAttrs = computed(() => {
  const rest: Record<string, unknown> = { ...attrs };
  delete rest.class;
  delete rest.style;
  return rest;
});
const layerStyle = computed(() =>
  props.paddingInline ? { paddingInline: props.paddingInline } : undefined
);

const inputElement = ref<HTMLInputElement | null>(null);
const mirror = ref<HTMLElement | null>(null);

/** The input scrolls by itself (typing, caret moves); the slot layer has to follow. */
function syncScroll() {
  if (inputElement.value && mirror.value) mirror.value.scrollLeft = inputElement.value.scrollLeft;
}
// A programmatic value change scrolls the input without a scroll event.
watch(model, () => void nextTick(syncScroll), { flush: 'post' });

defineExpose({ inputElement });
</script>

<template>
  <div
    class="nx-highlighted-text-input"
    :class="[`frame-${props.frame}`, { 'is-readonly': props.readonly }, attrs.class]"
    :style="attrs.style as never"
  >
    <span
      ref="mirror"
      class="nx-highlighted-text-input__text"
      :style="layerStyle"
      aria-hidden="true"
    >
      <slot />
    </span>
    <input
      ref="inputElement"
      v-bind="inputAttrs"
      v-model="model"
      type="text"
      class="nx-highlighted-text-input__field"
      :style="layerStyle"
      :placeholder="props.placeholder"
      :readonly="props.readonly"
      :aria-readonly="props.readonly || undefined"
      @input="syncScroll"
      @keyup="syncScroll"
      @click="syncScroll"
      @select="syncScroll"
      @scroll="syncScroll"
    />
  </div>
</template>

<style scoped>
.nx-highlighted-text-input {
  position: relative;
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  min-width: 0;
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
}
.nx-highlighted-text-input.frame-field {
  min-height: var(--nxt-control-size-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
}
.nx-highlighted-text-input.frame-field:focus-within {
  border-color: var(--nxt-brand-bg);
  box-shadow: 0 0 0 calc(var(--nxt-space-2xs) / 2) var(--nxt-focus-ring-soft);
}
.nx-highlighted-text-input.is-readonly {
  background: var(--nxt-surface-sunken-bg);
}

.nx-highlighted-text-input__text,
.nx-highlighted-text-input__field {
  box-sizing: border-box;
  padding-block: 0;
  padding-inline: var(--nxt-space-xs);
  font: inherit;
  letter-spacing: inherit;
}

.nx-highlighted-text-input__text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: pre;
  color: inherit;
  pointer-events: none;
  user-select: none;
}
:slotted(*) {
  flex: none;
}
.is-readonly > .nx-highlighted-text-input__text {
  visibility: hidden;
}

.nx-highlighted-text-input__field {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  margin: 0;
  border: none;
  outline: none;
  background: transparent;
  color: transparent;
  caret-color: var(--nxt-fg);
}
.nx-highlighted-text-input__field::placeholder {
  color: var(--nxt-fg-subtle);
  -webkit-text-fill-color: var(--nxt-fg-subtle);
}
.is-readonly > .nx-highlighted-text-input__field {
  color: var(--nxt-fg-muted);
  -webkit-text-fill-color: var(--nxt-fg-muted);
  caret-color: transparent;
  cursor: not-allowed;
}
</style>
